import React, { useState } from 'react'
import { updateApplication } from '@/api/tapApplications'
import { TapApplication, TapApplicationType } from '@/types/index'
import StageBadge from './StageBadge'
import StatusFlagBadge from './StatusFlagBadge'
import SlaIndicator from './SlaIndicator'

// Bug #167 — inline-edit block for the core application fields (position
// title, vacancy code, application type). Previously these were display-only
// on the Summary tab, which meant any typo or misclassification required a
// DB edit. Stage/flag/assignee/decision already have dedicated modals in
// ApplicationDetail, so those stay read-only here and link out to the top-bar
// actions.
interface ApplicationDetailsEditorProps {
  application: TapApplication
  onUpdated: (app: TapApplication) => void
  formatDate: (dateString?: string) => string
}

const cardStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-white)',
  border: '1px solid var(--color-light-grey)',
  borderRadius: '8px',
  padding: '20px',
  marginBottom: '16px',
}

const fieldRowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '16px',
  marginBottom: '16px',
}

const fieldLabelStyle: React.CSSProperties = {
  fontSize: '12px',
  fontWeight: 600,
  color: 'var(--color-dark-grey)',
  textTransform: 'uppercase',
  marginBottom: '4px',
}

const fieldValueStyle: React.CSSProperties = {
  fontSize: '14px',
  color: 'var(--color-black)',
  marginTop: '4px',
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  border: '1px solid var(--color-light-grey)',
  borderRadius: '6px',
  fontSize: '14px',
  fontFamily: 'var(--font-family)',
}

const ApplicationDetailsEditor: React.FC<ApplicationDetailsEditorProps> = ({
  application,
  onUpdated,
  formatDate,
}) => {
  const [editing, setEditing] = useState(false)
  // Feature #244: Position Title moved out of this editor into
  // SummaryEditor (the Review Notes card on the Summary tab) — it's a
  // review-time field for the HR Agent, not application metadata.
  const [vacancyCode, setVacancyCode] = useState(application.vacancyCode || '')
  const [applicationType, setApplicationType] = useState<TapApplicationType>(application.applicationType)
  const [saving, setSaving] = useState(false)

  const reset = () => {
    setVacancyCode(application.vacancyCode || '')
    setApplicationType(application.applicationType)
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const updated = await updateApplication(application.id, {
        // Bug #167: include application_type so recruiters can reclassify.
        applicationType,
        // Send explicit empty strings as undefined so Pydantic leaves the
        // column untouched rather than writing an empty string.
        vacancyCode: vacancyCode.trim() || undefined,
      })
      onUpdated(updated)
      setEditing(false)
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      alert(detail || 'Failed to update application details')
    } finally {
      setSaving(false)
    }
  }

  if (!editing) {
    return (
      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: 'var(--color-black)' }}>
            Application Details
          </h3>
          <button
            onClick={() => {
              reset()
              setEditing(true)
            }}
            style={{
              padding: '6px 12px',
              backgroundColor: 'var(--color-white)',
              color: 'var(--color-black)',
              border: '1px solid var(--color-light-grey)',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: 500,
              cursor: 'pointer',
              fontFamily: 'var(--font-family)',
            }}
          >
            Edit
          </button>
        </div>
        <div style={fieldRowStyle}>
          <div>
            <div style={fieldLabelStyle}>Reference Number</div>
            <div style={fieldValueStyle}>{application.referenceNumber}</div>
          </div>
          <div>
            <div style={fieldLabelStyle}>Application Type</div>
            <div style={fieldValueStyle}>
              {application.applicationType === 'JobApplication' ? 'Job Application' : 'Expression of Interest'}
            </div>
          </div>
        </div>
        {/* Feature #244: Position Title moved to the Review Notes card on
            the Summary tab (HR Agent fills it during review). The slot it
            used to share with Vacancy Code is now Vacancy Code on its own
            — kept at half-width so the rest of the 2-column metadata grid
            below remains untouched. */}
        <div style={fieldRowStyle}>
          <div>
            <div style={fieldLabelStyle}>Vacancy Code</div>
            <div style={fieldValueStyle}>{application.vacancyCode || '-'}</div>
          </div>
          <div />
        </div>
        <div style={fieldRowStyle}>
          <div>
            <div style={fieldLabelStyle}>Current Stage</div>
            <div style={{ marginTop: '4px' }}><StageBadge stage={application.stage} /></div>
          </div>
          <div>
            <div style={fieldLabelStyle}>Status Flag</div>
            <div style={{ marginTop: '4px' }}>
              {application.statusFlag ? <StatusFlagBadge flag={application.statusFlag} /> : '-'}
            </div>
          </div>
        </div>
        <div style={fieldRowStyle}>
          <div>
            <div style={fieldLabelStyle}>Decision Outcome</div>
            <div style={fieldValueStyle}>{application.decisionOutcome || '-'}</div>
          </div>
          <div>
            <div style={fieldLabelStyle}>SLA Status</div>
            <div style={{ display: 'flex', alignItems: 'center', marginTop: '4px' }}>
              <SlaIndicator status={application.slaStatus} />
              <span style={fieldValueStyle}>{application.slaStatus || '-'}</span>
            </div>
          </div>
        </div>
        <div style={fieldRowStyle}>
          <div>
            <div style={fieldLabelStyle}>Assigned To</div>
            {/* Bug #168: show display name instead of raw UUID */}
            <div style={fieldValueStyle}>{application.assignedToDisplayName || '-'}</div>
          </div>
          <div>
            <div style={fieldLabelStyle}>Received</div>
            <div style={fieldValueStyle}>{formatDate(application.receivedAt)}</div>
          </div>
        </div>
        {/* Feature #216 / #217: Location + Availability */}
        <div style={fieldRowStyle}>
          <div>
            <div style={fieldLabelStyle}>Location</div>
            <div style={fieldValueStyle}>{application.applicant?.location || '-'}</div>
          </div>
          <div>
            <div style={fieldLabelStyle}>Availability</div>
            <div style={fieldValueStyle}>{application.availability || '-'}</div>
          </div>
        </div>
        <div style={fieldRowStyle}>
          <div>
            <div style={fieldLabelStyle}>Source System</div>
            <div style={fieldValueStyle}>{application.sourceSystem || '-'}</div>
          </div>
          <div>
            <div style={fieldLabelStyle}>Source ID</div>
            <div style={fieldValueStyle}>{application.sourceId || '-'}</div>
          </div>
        </div>
        <div style={{ fontSize: '12px', color: 'var(--color-dark-grey)' }}>
          Stage, flag, assignee, and decision are changed via the buttons at the top of the page.
        </div>
      </div>
    )
  }

  return (
    <div style={cardStyle}>
      <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600, color: 'var(--color-black)' }}>
        Application Details
      </h3>
      <div style={fieldRowStyle}>
        <div>
          <div style={fieldLabelStyle}>Reference Number</div>
          <div style={fieldValueStyle}>{application.referenceNumber}</div>
        </div>
        <div>
          <label style={fieldLabelStyle}>Application Type</label>
          <select
            value={applicationType}
            onChange={(e) => setApplicationType(e.target.value as TapApplicationType)}
            style={{ ...inputStyle, backgroundColor: 'var(--color-white)', cursor: 'pointer' }}
          >
            <option value="JobApplication">Job Application</option>
            <option value="ExpressionOfInterest">Expression of Interest</option>
          </select>
        </div>
      </div>
      {/* Feature #244: Position Title field is no longer in this editor —
          it's owned by SummaryEditor (Review Notes) on the Summary tab. */}
      <div style={fieldRowStyle}>
        <div>
          <label style={fieldLabelStyle}>Vacancy Code</label>
          <input
            type="text"
            value={vacancyCode}
            onChange={(e) => setVacancyCode(e.target.value)}
            placeholder="e.g. VAC-2026-0142"
            style={inputStyle}
          />
        </div>
        <div />
      </div>
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
        <button
          onClick={() => {
            reset()
            setEditing(false)
          }}
          disabled={saving}
          style={{
            padding: '8px 16px',
            backgroundColor: 'var(--color-white)',
            color: 'var(--color-black)',
            border: '1px solid var(--color-light-grey)',
            borderRadius: '6px',
            fontSize: '13px',
            fontWeight: 500,
            cursor: 'pointer',
            fontFamily: 'var(--font-family)',
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            padding: '8px 16px',
            backgroundColor: 'var(--color-magenta)',
            color: 'var(--color-white)',
            border: 'none',
            borderRadius: '6px',
            fontSize: '13px',
            fontWeight: 500,
            cursor: saving ? 'not-allowed' : 'pointer',
            opacity: saving ? 0.6 : 1,
            fontFamily: 'var(--font-family)',
          }}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  )
}

export default ApplicationDetailsEditor
